import { CheckCircleIcon, XCircleIcon } from "@heroicons/react/24/outline";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import Card from "../components/shared/card";
import ThemedSpinner from "../components/shared/spinner";
import Typography from "../components/shared/typography";

const AccessRequestRejectPage = () => {
  const { accessRejectJwt } = useParams<{ accessRejectJwt: string }>();
  const [isLoading, setIsLoading] = useState(true);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const rejectAccess = async () => {
      if (!accessRejectJwt) {
        setError("Invalid access request link");
        setIsLoading(false);
        return;
      }

      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/access-request/reject/${accessRejectJwt}`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
            },
          }
        );

        if (response.ok) {
          setIsSuccess(true);
        } else {
          const errorData = await response.json();
          setError(errorData.message || "Failed to reject access");
        }
      } catch (err) {
        console.error("Error rejecting access:", err);
        setError("Failed to reject access. Please try again.");
      } finally {
        setIsLoading(false);
      }
    };

    rejectAccess();
  }, [accessRejectJwt]);

  if (isLoading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-md mx-auto">
          <Card variant="cream" elevation={2}>
            <Card.Body className="text-center py-8">
              <ThemedSpinner size="lg" />
              <Typography variant="h5" className="mt-4 text-primary">
                Processing Access Request...
              </Typography>
              <Typography variant="body2" className="mt-2 text-gray-600">
                Please wait while we reject the access request.
              </Typography>
            </Card.Body>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="max-w-md mx-auto">
        <Card variant="cream" elevation={2}>
          <Card.Body className="text-center py-8">
            {isSuccess ? (
              <>
                <CheckCircleIcon className="w-16 h-16 text-green-500 mx-auto mb-4" />
                <Typography variant="h4" className="text-green-600 mb-2">
                  Access Rejected!
                </Typography>
                <Typography variant="body1" className="text-gray-600 mb-4">
                  The access request has been successfully rejected.
                </Typography>
                <a
                  href="/"
                  className="inline-block px-6 py-2 bg-terracotta text-white rounded-lg hover:bg-terracotta-700 transition-colors"
                >
                  Go to Home
                </a>
              </>
            ) : (
              <>
                <XCircleIcon className="w-16 h-16 text-red-500 mx-auto mb-4" />
                <Typography variant="h4" className="text-red-600 mb-2">
                  Rejection Failed
                </Typography>
                <Typography variant="body1" className="text-gray-600 mb-4">
                  {error ||
                    "There was an error processing your access request rejection."}
                </Typography>
                <a
                  href="/"
                  className="inline-block px-6 py-2 bg-terracotta text-white rounded-lg hover:bg-terracotta-700 transition-colors"
                >
                  Go to Home
                </a>
              </>
            )}
          </Card.Body>
        </Card>
      </div>
    </div>
  );
};

export default AccessRequestRejectPage;
